import React, { useRef } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type CatalogFlowTrophyBadgeVariant = 'quiz-passed' | 'exercise-reviewed';

const trophyFallBackClass = 'h-9 w-9 text-green-600 dark:text-emerald-300';
/** Matches TagContentView passed row fallback. */
const passedFallBackClass = 'h-8 w-8 shrink-0 text-green-600 dark:text-green-400';

interface CatalogFlowTrophyBadgeProps {
  variant: CatalogFlowTrophyBadgeVariant;
  /** When quiz-passed: Trophy.lottie HEAD failed. When exercise-reviewed: Passed.lottie HEAD failed. */
  lottieUnavailable: boolean;
}

interface CatalogFlowQuizTrophyIconProps {
  lottieUnavailable: boolean;
  className?: string;
}

type ReplayableLottieSize = 'trophy' | 'tick';

/** Trophy 64/80px. Tick is 30% smaller: 44/56px. */
const replayableLottieSizeClass: Record<ReplayableLottieSize, { box: string; canvas: string }> = {
  trophy: {
    box: 'h-16 w-16 min-h-16 min-w-16 max-h-16 max-w-16 -my-2 sm:my-0 sm:h-20 sm:w-20 sm:min-h-20 sm:min-w-20 sm:max-h-20 sm:max-w-20',
    canvas:
      'h-16 w-16 max-h-16 max-w-16 sm:h-20 sm:w-20 sm:max-h-20 sm:max-w-20 [&_canvas]:h-16 [&_canvas]:w-16 [&_canvas]:max-h-16 [&_canvas]:max-w-16 sm:[&_canvas]:h-20 sm:[&_canvas]:w-20 sm:[&_canvas]:max-h-20 sm:[&_canvas]:max-w-20',
  },
  tick: {
    box: 'h-11 w-11 min-h-11 min-w-11 max-h-11 max-w-11 sm:h-14 sm:w-14 sm:min-h-14 sm:min-w-14 sm:max-h-14 sm:max-w-14',
    canvas:
      'h-11 w-11 max-h-11 max-w-11 sm:h-14 sm:w-14 sm:max-h-14 sm:max-w-14 [&_canvas]:h-11 [&_canvas]:w-11 [&_canvas]:max-h-11 [&_canvas]:max-w-11 sm:[&_canvas]:h-14 sm:[&_canvas]:w-14 sm:[&_canvas]:max-h-14 sm:[&_canvas]:max-w-14',
  },
};

interface ReplayableCatalogLottieIconProps {
  lottieUnavailable: boolean;
  className?: string;
  src: string;
  fallbackClass: string;
  ariaLabel: string;
  size?: ReplayableLottieSize;
}

const ReplayableCatalogLottieIcon: React.FC<ReplayableCatalogLottieIconProps> = ({
  lottieUnavailable,
  className = '',
  src,
  fallbackClass,
  ariaLabel,
  size = 'trophy',
}) => {
  const lottieRef = useRef<{ stop: () => void; play: () => void } | null>(null);
  const dims = replayableLottieSizeClass[size];

  const replay = () => {
    const player = lottieRef.current;
    if (player) {
      player.stop();
      player.play();
    }
  };

  const boxClass = `flex ${dims.box} shrink-0 grow-0 self-center aspect-square items-center justify-center overflow-hidden p-0 ${className}`.trim();

  if (lottieUnavailable) {
    return (
      <div className={boxClass} aria-hidden>
        <CheckCircle2 className={fallbackClass} />
      </div>
    );
  }

  return (
    <div
      className={`${boxClass} cursor-pointer select-none`}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={replay}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          replay();
        }
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        replay();
      }}
    >
      <DotLottieReact
        src={src}
        loop={false}
        autoplay
        className={`pointer-events-none shrink-0 object-contain ${dims.canvas} [&_canvas]:object-contain`}
        dotLottieRefCallback={(instance: { stop: () => void; play: () => void } | null) => {
          lottieRef.current = instance;
        }}
      />
    </div>
  );
};

/**
 * Same Trophy.lottie as lesson viewer: tap/click replays.
 * 64px beside the title on mobile; 80px and vertically centered with the action buttons from `sm` up.
 */
export const CatalogFlowQuizTrophyIcon: React.FC<CatalogFlowQuizTrophyIconProps> = ({
  lottieUnavailable,
  className = '',
}) => {
  const { t } = useTranslation('learning');

  return (
    <ReplayableCatalogLottieIcon
      lottieUnavailable={lottieUnavailable}
      className={className}
      src="/animations/Trophy.lottie"
      fallbackClass={trophyFallBackClass}
      ariaLabel={t('lesson.replayTrophy', { defaultValue: 'Replay trophy animation' })}
    />
  );
};

interface CatalogFlowExercisePassedIconProps {
  lottieUnavailable: boolean;
  className?: string;
}

/**
 * Same Passed.lottie as exercise reviewed row: tap/click replays.
 * 30% smaller than the quiz trophy (44px mobile / 56px desktop).
 */
export const CatalogFlowExercisePassedIcon: React.FC<CatalogFlowExercisePassedIconProps> = ({
  lottieUnavailable,
  className = '',
}) => {
  const { t } = useTranslation('learning');

  return (
    <ReplayableCatalogLottieIcon
      lottieUnavailable={lottieUnavailable}
      className={className}
      src="/animations/Passed.lottie"
      fallbackClass={passedFallBackClass}
      ariaLabel={t('lesson.replayTrophy', { defaultValue: 'Replay trophy animation' })}
      size="tick"
    />
  );
};

/**
 * Quiz passed: Congratulations + success description. Exercise reviewed: Passed.lottie + inline label as tag “passed” row.
 */
export const CatalogFlowTrophyBadge: React.FC<CatalogFlowTrophyBadgeProps> = ({ variant }) => {
  const { t } = useTranslation('learning');

  if (variant === 'exercise-reviewed') {
    return (
      <p className="mt-2 text-start text-xs font-medium leading-snug text-green-700 dark:text-green-400">
        {t('exerciseFlow.reviewedBadge')}
      </p>
    );
  }

  return (
    <p className="mt-2 text-start text-xs leading-snug text-green-900 dark:text-emerald-200">
      <span className="font-bold">{t('catalogFlow.quizPassedCongrats')}</span>{' '}
      <span className="font-medium">
        {t('catalogFlow.quizPassedSuccessfully', { defaultValue: 'You passed this quiz successfully.' })}
      </span>
    </p>
  );
};
